import React, { useState, useEffect } from 'react';
import api from '../utils/apiInterceptor';
import {
  Box, Tabs, Tab, Grid, Stack, Typography, Chip,
  LinearProgress, Alert, AlertTitle,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BoltIcon from '@mui/icons-material/Bolt';
import StarIcon from '@mui/icons-material/Star';

/* ===================== 配色（与关系规划一致） ===================== */
const C = {
  bg:       '#080c14',
  surface:   '#0d1220',
  border:    'rgba(255,255,255,0.06)',
  text:      'rgba(255,255,255,0.88)',
  textSec:   'rgba(255,255,255,0.42)',
  textDim:   'rgba(255,255,255,0.22)',
  accent:    '#00d4b8',
  accentDim: 'rgba(0,212,184,0.12)',
  accentBor: 'rgba(0,212,184,0.28)',
  amber:     '#fbbf24',
  amberDim:  'rgba(251,191,36,0.12)',
  amberBor:  'rgba(251,191,36,0.28)',
  red:       '#f87171',
  redDim:    'rgba(248,113,113,0.12)',
  redBor:    'rgba(248,113,113,0.28)',
  purple:    '#c084fc',
  purpleDim: 'rgba(192,132,252,0.12)',
  purpleBor: 'rgba(192,132,252,0.28)',
};

const getIntensityColor = (intensity) => {
  if (intensity >= 9) return C.red;
  if (intensity >= 7) return '#fb923c';
  if (intensity >= 5) return C.amber;
  if (intensity >= 3) return '#4ade80';
  return C.accent;
};

const getIntensityLabel = (intensity) => {
  if (intensity >= 9) return '极强';
  if (intensity >= 7) return '强烈';
  if (intensity >= 5) return '中等';
  if (intensity >= 3) return '较弱';
  return '微弱';
};

/* ===================== 主组件 ===================== */
const EmotionalArcViewer = ({ novelId }) => {
  const [emotionalArc, setEmotionalArc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [charTab, setCharTab] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 960);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (novelId) fetchEmotionalArc();
  }, [novelId]);

  const fetchEmotionalArc = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/novels/${novelId}/emotional-arc`);
      if (response.data.success) {
        const data = response.data.data || {};
        setEmotionalArc(data);
        // protagonist 是数组，配角也是数组，全部扁平化
        const protagonists = Array.isArray(data.protagonist) ? data.protagonist : [];
        const supporting = Array.isArray(data.supportingCharacters) ? data.supportingCharacters : [];
        const allChars = [...protagonists, ...supporting].filter(Boolean);
        if (allChars.length > 0) {
          setSelectedCharacter(allChars[0]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || '获取情感弧线失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: C.bg, minHeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <LinearProgress sx={{ width: 200, '& .MuiLinearProgress-bar': { bgcolor: C.accent }, bgcolor: C.accentDim }} />
        <Typography sx={{ color: C.textSec, fontSize: '0.82rem' }}>加载情感弧线...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: C.bg, p: 2 }}>
        <Alert severity="error" sx={{ bgcolor: C.redDim, border: `1px solid ${C.redBor}`, '& .MuiAlert-icon': { color: C.red } }}>
          <AlertTitle sx={{ color: C.red }}>加载失败</AlertTitle>
          {error}
          <Box component="button" onClick={fetchEmotionalArc}
            sx={{ color: C.red, cursor: 'pointer', border: 'none', bgcolor: 'transparent', fontSize: '0.875rem', fontWeight: 600, display: 'block', mt: 1 }}>
            点击重试
          </Box>
        </Alert>
      </Box>
    );
  }

  const protagonistArr = Array.isArray(emotionalArc?.protagonist) ? emotionalArc.protagonist : [];
  const supportingCharacters = Array.isArray(emotionalArc?.supportingCharacters) ? emotionalArc.supportingCharacters : [];
  const allCharacters = [...protagonistArr, ...supportingCharacters].filter(Boolean);
  const currentChar = selectedCharacter;

  // 空数据引导页
  if (!emotionalArc || allCharacters.length === 0) {
    return (
      <Box sx={{ bgcolor: C.bg, minHeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%',
            bgcolor: C.accentDim, border: `2px solid ${C.accentBor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 3,
          }}>
            <FavoriteIcon sx={{ fontSize: 36, color: C.accent }} />
          </Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: C.text, mb: 1 }}>情感弧线规划</Typography>
          <Typography sx={{ color: C.textSec, fontSize: '0.9rem', lineHeight: 1.8, mb: 4 }}>
            AI智能设计角色情感变化曲线<br />请先在「故事结构」中生成故事结构
          </Typography>
          <Alert severity="info" sx={{ bgcolor: C.accentDim, border: `1px solid ${C.accentBor}`, textAlign: 'left', '& .MuiAlert-icon': { color: C.accent }, '& .MuiAlertTitle-root': { color: C.accent } }}>
            <AlertTitle>功能说明</AlertTitle>
            <Box component="ul" sx={{ mt: 1, pl: 2, color: C.textSec, fontSize: '0.82rem', lineHeight: 2 }}>
              <li>整体情感路径：角色全篇情感走向</li>
              <li>情感变化阶段：各章节情感细分</li>
              <li>情感高峰时刻：故事中的情感爆发点</li>
            </Box>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: C.bg, width: '100%', minHeight: 'calc(100vh - 200px)', color: C.text }}>
      {isMobile ? (
        <MobileLayout
          allCharacters={allCharacters}
          currentChar={currentChar}
          setSelectedCharacter={setSelectedCharacter}
          charTab={charTab}
          setCharTab={setCharTab}
          protagonistArr={protagonistArr}
        />
      ) : (
        <PCLayout
          allCharacters={allCharacters}
          currentChar={currentChar}
          setSelectedCharacter={setSelectedCharacter}
          protagonistArr={protagonistArr}
        />
      )}
    </Box>
  );
};

/* ===================== PC 布局 ===================== */
const PCLayout = ({ allCharacters, currentChar, setSelectedCharacter, protagonistArr }) => {
  const protagonistNames = protagonistArr.map(p => p?.name).filter(Boolean);

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
      {/* 左侧角色边栏 */}
      <Box sx={{ width: 240, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', bgcolor: C.surface }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <PersonIcon sx={{ fontSize: 18, color: C.accent }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>角色列表</Typography>
            <Chip label={`${allCharacters.length}人`} size="small" sx={{
              height: 18, fontSize: '0.62rem', ml: 'auto',
              bgcolor: C.accentDim, color: C.accent, border: `1px solid ${C.accentBor}`,
            }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <MiniStat icon={<StarIcon sx={{ fontSize: 11 }} />} value={allCharacters.length} label="角色" />
            <MiniStat icon={<BoltIcon sx={{ fontSize: 11 }} />} value={currentChar?.emotionalPeaks?.length || 0} label="高峰" />
          </Stack>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {allCharacters.map((char) => {
            const isSelected = currentChar?.name === char.name;
            const isProtagonist = protagonistNames.includes(char.name);
            return (
              <Box key={char.name} onClick={() => setSelectedCharacter(char)}
                sx={{
                  px: 2, py: 1.25, borderBottom: `1px solid ${C.border}`,
                  cursor: 'pointer', transition: 'all 0.15s',
                  bgcolor: isSelected ? C.accentDim : 'transparent',
                  borderLeft: isSelected ? `3px solid ${C.accent}` : `3px solid transparent`,
                  '&:hover': { bgcolor: isSelected ? C.accentDim : 'rgba(255,255,255,0.02)' },
                }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FavoriteIcon sx={{ fontSize: 14, color: isSelected ? C.accent : C.textDim, flexShrink: 0 }} />
                  <Typography sx={{
                    fontSize: '0.82rem', fontWeight: isSelected ? 700 : 400,
                    color: isSelected ? C.accent : C.textSec, flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {char.name || '未命名角色'}
                  </Typography>
                  {isProtagonist && (
                    <Chip label="主角" size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: C.purpleDim, color: C.purple }} />
                  )}
                </Stack>
                {char.detailedCurve?.length > 0 && (
                  <Typography sx={{ fontSize: '0.62rem', color: C.textDim, mt: 0.5, pl: 3 }}>
                    {char.detailedCurve.length}阶段 · {char.emotionalPeaks?.length || 0}高峰
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* 右侧详情 */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {currentChar ? (
          <PCCharDetail char={currentChar} isProtagonist={protagonistNames.includes(currentChar?.name)} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.9rem' }}>请从左侧选择角色查看情感弧线</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const MiniStat = ({ icon, value, label }) => (
  <Box sx={{ flex: 1, textAlign: 'center' }}>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.3}>
      <Box sx={{ color: C.textDim, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.text }}>{value}</Typography>
    </Stack>
    <Typography sx={{ fontSize: '0.62rem', color: C.textDim }}>{label}</Typography>
  </Box>
);

/* ===================== PC 角色详情 ===================== */
const PCCharDetail = ({ char, isProtagonist }) => {
  const [tab, setTab] = useState(0);
  const curve = char.detailedCurve || [];
  const peaks = char.emotionalPeaks || [];
                  
                  return (
    <Box>
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${C.border}`, bgcolor: 'rgba(0,212,184,0.03)' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FavoriteIcon sx={{ fontSize: 26, color: C.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', flex: 1 }}>
            {char?.name || '未命名角色'}
          </Typography>
          {isProtagonist && (
            <Chip label="主角" size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: C.purpleDim, color: C.purple, border: `1px solid ${C.purpleBor}` }}
            />
          )}
        </Stack>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${C.border}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
          sx={{
            '& .MuiTab-root': { color: C.textDim, minHeight: 48, fontSize: '0.78rem', '&.Mui-selected': { color: C.accent } },
            '& .MuiTabs-indicator': { bgcolor: C.accent },
          }}
        >
          <Tab label="路径" icon={<StarIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="阶段" icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="高峰" icon={<BoltIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {tab === 0 && <PCOverview char={char} />}
        {tab === 1 && <PCStages curve={curve} />}
        {tab === 2 && <PCPeaks peaks={peaks} />}
      </Box>
    </Box>
  );
};

const PCOverview = ({ char }) => (
  <Box>
    {char.overallArc ? (
      <Box sx={{ p: 2.5, borderRadius: 1.5, border: `1px solid ${C.accentBor}`, bgcolor: C.surface }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <StarIcon sx={{ fontSize: 18, color: C.amber }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.amber }}>整体情感路径</Typography>
        </Stack>
        <Typography sx={{ color: C.textSec, fontSize: '0.88rem', lineHeight: 1.9 }}>{char.overallArc}</Typography>
      </Box>
    ) : (
      <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无整体路径</Typography></Box>
    )}
    {char.emotionalTrend && (
      <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, border: `1px solid ${C.border}`, bgcolor: C.surface }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 1 }}>情感基调</Typography>
        <Typography sx={{ color: C.textSec, fontSize: '0.85rem' }}>{char.emotionalTrend}</Typography>
      </Box>
    )}
  </Box>
);

const PCStages = ({ curve }) => {
  if (!curve.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无情感阶段</Typography></Box>;
  return (
    <Grid container spacing={1.5}>
      {curve.map((c, idx) => (
        <Grid item xs={12} sm={6} key={idx}>
          <Box sx={{ borderLeft: `3px solid ${C.accent}`, pl: 2, py: 0.5, borderBottom: `1px solid ${C.border}`, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: C.accent, color: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                {idx + 1}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', flex: 1 }}>{c.emotion || '情感阶段'}</Typography>
              {c.chapterRange?.length === 2 && (
                <Chip label={`${c.chapterRange[0]}-${c.chapterRange[1]}章`} size="small"
                  sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.accentDim, color: C.accent }} />
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, pl: 3.5 }}>
              <Typography sx={{ color: C.textDim, fontSize: '0.7rem' }}>强度</Typography>
              <Box sx={{ flex: 1, height: 5, bgcolor: C.accentDim, borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${(c.intensity || 0) * 10}%`, bgcolor: getIntensityColor(c.intensity || 0), borderRadius: 1 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: getIntensityColor(c.intensity || 0) }}>{c.intensity || 0}</Typography>
              <Chip label={getIntensityLabel(c.intensity || 0)} size="small"
                sx={{ height: 16, fontSize: '0.6rem', color: getIntensityColor(c.intensity || 0), bgcolor: 'rgba(255,255,255,0.06)' }} />
            </Stack>

            {c.emotionalTransition && (
              <Typography sx={{ pl: 3.5, color: C.textSec, fontSize: '0.8rem' }}>
                → {c.emotionalTransition}
              </Typography>
            )}
            {c.keyEvents?.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1, pl: 3.5 }}>
                {c.keyEvents.map((e, ei) => (
                  <Typography key={ei} sx={{ color: C.textSec, fontSize: '0.78rem' }}>
                    <Box component="span" sx={{ color: C.accent, fontWeight: 700 }}>第{e.chapter}章</Box> {e.event}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

const PCPeaks = ({ peaks }) => {
  if (!peaks.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无情感高峰</Typography></Box>;
  return (
    <Grid container spacing={1.5}>
      {peaks.map((p, idx) => (
        <Grid item xs={12} sm={6} key={idx}>
          <Box sx={{ borderLeft: `3px solid ${C.red}`, pl: 1.5, py: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Chip label={`第${p.chapter}章`} size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.redDim, color: C.red, border: `1px solid ${C.redBor}`, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: C.red, mb: 0.25 }}>{p.emotion || '情感高峰'}</Typography>
                <Typography sx={{ color: C.textSec, fontSize: '0.82rem' }}>{p.event}</Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getIntensityColor(p.intensity || 0) }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: getIntensityColor(p.intensity || 0) }}>{p.intensity || 0}</Typography>
              </Stack>
            </Stack>
            {p.description && (
              <Typography sx={{ pl: 5, color: C.textDim, fontSize: '0.78rem', mt: 0.5 }}>{p.description}</Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

/* ===================== 移动端布局 ===================== */
const MobileLayout = ({ allCharacters, currentChar, setSelectedCharacter, charTab, setCharTab, protagonistArr }) => {
  const protagonistNames = protagonistArr.map(p => p?.name).filter(Boolean);
  const curve = currentChar?.detailedCurve || [];
  const peaks = currentChar?.emotionalPeaks || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 200px)' }}>
      {/* 角色网格选择 */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.border}`, bgcolor: C.surface }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <PersonIcon sx={{ fontSize: 14, color: C.accent }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.textSec }}>角色</Typography>
          <Chip label={`${allCharacters.length}人`} size="small" sx={{
            height: 16, fontSize: '0.6rem', ml: 'auto',
            bgcolor: C.accentDim, color: C.accent, border: `1px solid ${C.accentBor}`,
          }} />
        </Stack>
        <Grid container spacing={0.75}>
          {allCharacters.map((char) => {
            const isSelected = currentChar?.name === char.name;
            const isProtagonist = protagonistNames.includes(char.name);
            return (
              <Grid item xs={6} key={char.name}>
                <Box
                  onClick={() => { setSelectedCharacter(char); setCharTab(0); }}
                  sx={{
                    p: 1, borderRadius: 1, cursor: 'pointer',
                    border: `1px solid ${isSelected ? C.accentBor : C.border}`,
                    bgcolor: isSelected ? C.accentDim : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <FavoriteIcon sx={{ fontSize: 12, color: isSelected ? C.accent : C.textDim, flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: '0.72rem', fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? C.accent : C.textSec,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                    }}>
                      {char.name || '未命名角色'}
                    </Typography>
                  </Stack>
                  <Chip label={isProtagonist ? '主角' : '配角'} size="small"
                    sx={{ height: 14, fontSize: '0.58rem', color: isProtagonist ? C.purple : C.textDim, bgcolor: isProtagonist ? C.purpleDim : 'transparent' }} />
                </Box>
              </Grid>
                  );
                })}
        </Grid>
      </Box>

      {/* 当前角色头部 */}
      {currentChar && (
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}`, bgcolor: 'rgba(0,212,184,0.02)' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FavoriteIcon sx={{ fontSize: 18, color: C.accent }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', flex: 1 }}>{currentChar.name}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip label={`${curve.length}阶段`} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.accentDim, color: C.accent }} />
              <Chip label={`${peaks.length}高峰`} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.redDim, color: C.red }} />
            </Stack>
          </Stack>
        </Box>
      )}

      {/* 移动端 Tab */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: C.surface, borderBottom: `1px solid ${C.border}`,
        borderTop: `1px solid ${C.border}`,
      }}>
        <Tabs value={charTab} onChange={(_, v) => setCharTab(v)} variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': { color: C.textDim, minHeight: 48, fontSize: '0.68rem', py: 0, '&.Mui-selected': { color: C.accent } },
            '& .MuiTabs-indicator': { bgcolor: C.accent },
          }}
        >
          <Tab label="路径" icon={<StarIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          <Tab label="阶段" icon={<EmojiEventsIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          <Tab label="高峰" icon={<BoltIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* 内容 */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.5 }}>
        {!currentChar ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>点击上方角色卡片查看详情</Typography>
          </Box>
        ) : charTab === 0 ? (
          <MobileOverview char={currentChar} />
        ) : charTab === 1 ? (
          <MobileStages curve={curve} />
        ) : (
          <MobilePeaks peaks={peaks} />
        )}
      </Box>
    </Box>
  );
};

const MobileOverview = ({ char }) => (
  <Box>
    {char.overallArc ? (
      <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.accentBor}`, bgcolor: C.surface, mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <StarIcon sx={{ fontSize: 16, color: C.amber }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.amber }}>整体情感路径</Typography>
        </Stack>
        <Typography sx={{ color: C.textSec, fontSize: '0.82rem', lineHeight: 1.9 }}>{char.overallArc}</Typography>
      </Box>
    ) : (
      <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>暂无整体路径</Typography></Box>
    )}
    {char.emotionalTrend && (
      <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.border}`, bgcolor: C.surface }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 0.5 }}>情感基调</Typography>
        <Typography sx={{ color: C.textSec, fontSize: '0.8rem' }}>{char.emotionalTrend}</Typography>
      </Box>
    )}
  </Box>
);

const MobileStages = ({ curve }) => {
  if (!curve.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无情感阶段</Typography></Box>;
  return (
    <Stack spacing={0}>
      {curve.map((c, idx) => (
        <Box key={idx} sx={{ position: 'relative', pb: idx < curve.length - 1 ? 2 : 0 }}>
          {idx < curve.length - 1 && (
            <Box sx={{ position: 'absolute', left: 11, top: 28, bottom: 0, width: 1, bgcolor: C.border }} />
          )}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: C.accent, color: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0, mt: 0.25 }}>
              {idx + 1}
            </Box>
            <Box sx={{ flex: 1, pb: 1.5, borderBottom: idx < curve.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{c.emotion || '情感阶段'}</Typography>
                {c.chapterRange?.length === 2 && (
                  <Chip label={`${c.chapterRange[0]}-${c.chapterRange[1]}章`} size="small"
                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: C.accentDim, color: C.accent }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ color: C.textDim, fontSize: '0.7rem' }}>强度</Typography>
                <Box sx={{ flex: 1, height: 5, bgcolor: C.accentDim, borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${(c.intensity || 0) * 10}%`, bgcolor: getIntensityColor(c.intensity || 0), borderRadius: 1 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: getIntensityColor(c.intensity || 0) }}>{c.intensity || 0}/10</Typography>
              </Stack>
              {c.emotionalTransition && (
                <Typography sx={{ color: C.textSec, fontSize: '0.75rem' }}>→ {c.emotionalTransition}</Typography>
              )}
              {c.keyEvents?.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  {c.keyEvents.map((e, ei) => (
                    <Typography key={ei} sx={{ color: C.textSec, fontSize: '0.75rem' }}>
                      <Box component="span" sx={{ color: C.accent, fontWeight: 700 }}>第{e.chapter}章</Box> {e.event}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const MobilePeaks = ({ peaks }) => {
  if (!peaks.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无情感高峰</Typography></Box>;
  return (
    <Stack spacing={1}>
      {peaks.map((p, idx) => (
        <Box key={idx} sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.redBor}`, bgcolor: C.redDim }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Chip label={`第${p.chapter}章`} size="small"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.redDim, color: C.red, border: `1px solid ${C.redBor}`, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.red }}>{p.emotion || '情感高峰'}</Typography>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getIntensityColor(p.intensity || 0), ml: 'auto' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: getIntensityColor(p.intensity || 0) }}>{p.intensity || 0}</Typography>
              </Stack>
              <Typography sx={{ color: C.textSec, fontSize: '0.78rem' }}>{p.event}</Typography>
              {p.description && (
                <Typography sx={{ color: C.textDim, fontSize: '0.72rem', mt: 0.5 }}>{p.description}</Typography>
              )}
            </Box>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default EmotionalArcViewer;
